import styles from './template.css';
import template from './template';
import {storeInstance} from '@aofl/store/dist/legacy';
import {AoflElement, customElement, property} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import translations from '@modules/i18n';
import {sdoNs, permissionGroupKeys, permissionGroups, exceptionCodes} from '@modules/constants-enumerate';
import {ThreeWayToggle} from '@modules/three-way-toggle';
import {restrictedPermissions} from '@modules/permissions';

const removeGroupPermission = (acc, item) => {
  acc[item] = false;
  return acc;
};

const addGroupPermission = (acc, item) => {
  acc[item] = true;
  return acc;
};

/**
 * @summary RoleCrudBlock
 * @extends {i18nMixin(AoflElement)}
 */
@customElement('role-crud-block')
class RoleCrudBlock extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of RoleCrudBlock.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;
  }

  /**
   * @readonly
   */
  static is = 'role-crud-block';

  @property({type: Number, mapState: `${sdoNs.SESSION}.group_info.id`, store: storeInstance})
  groupId = 0;

  @property({type: Number, mapState: `${sdoNs.SESSION}.settings.tier_structure.levels`, store: storeInstance})
  tiersLevels = 0;

  @property({type: Object, mapState: `${sdoNs.SESSION}.settings`, store: storeInstance})
  settings = {};

  @property({type: Number})
  selectedGroup = 0;

  @property({type: Number})
  selectedGroupLevel = 0;

  @property({type: Boolean})
  roleExists = false;

  @property({type: Boolean})
  isSponsor = false;

  @property({type: Boolean})
  isHideStudent = false;

  @property({type: Boolean})
  genericApiError = false;

  @property({type: Object})
  permissions = {
    [permissionGroupKeys.ADMIN_ACCOUNTS]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.ADMIN_ACCOUNTS].reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.MANUAL_ROSTER]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.MANUAL_ROSTER]
        .reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.ROSTER_UPLOAD]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.ROSTER_UPLOAD]
        .reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.LICENSE]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.LICENSE]
        .reduce(removeGroupPermission, {})
    },
    [permissionGroupKeys.SFTP]: {
      toggleState: ThreeWayToggle.VALUES.NONE,
      permissions: permissionGroups[permissionGroupKeys.SFTP]
        .reduce(removeGroupPermission, {})
    },
  };

  @property({type: Number})
  index = 0;

  @property({type: Object})
  role = null;

  groupChangeHandler(e) {
    const lastGroup = e.detail.groupInfo;
    this.selectedGroupLevel = lastGroup.path.split('.').length;
    this.selectedGroup = lastGroup.id;

    const disableAdminPermission = this.selectedGroupLevel >= this.tiersLevels;
    this.permissions[permissionGroupKeys.ADMIN_ACCOUNTS].disabled = disableAdminPermission;
    if (disableAdminPermission) {
      this.permissions[permissionGroupKeys.ADMIN_ACCOUNTS].toggleState = ThreeWayToggle.VALUES.NONE;
      this.permissions[permissionGroupKeys.ADMIN_ACCOUNTS].permissions['user.update'] = false;
    }
    this.save();
  }

  dismiss(e) {
    this.dispatchEvent(new CustomEvent('dismiss-role'));
    this.save();
  }

  rosterChangeHandler(e) {
    const selected = e.target.selected;
    const allCount = permissionGroups[permissionGroupKeys.MANUAL_ROSTER].length;
    const toggleState = selected.length === allCount?
      ThreeWayToggle.VALUES.ALL: selected.length === 0?
      ThreeWayToggle.NONE: ThreeWayToggle.VALUES.SOME;
    const permissions = {
      toggleState,
      permissions: permissionGroups[permissionGroupKeys.MANUAL_ROSTER]
        .reduce(removeGroupPermission, {})
    };

    for (let i = 0; i < selected.length; i++) {
      const selection = selected[i];
      if (typeof permissions.permissions[selection] !== 'undefined') {
        permissions.permissions[selection] = true;
      }
    }

    this.permissions = Object.assign({}, this.permissions, {
      [permissionGroupKeys.MANUAL_ROSTER]: permissions
    });
    this.save();
  }

  setPermission(e) {
    const permissionGroupKey = e.target.dataset.permissionGroupKey;
    const state = e.target.value;

    let permission = {};

    if (state === ThreeWayToggle.VALUES.ALL) {
      // if turning on SFTP, also turn on ROSTER_UPLOAD
      if (permissionGroupKey === permissionGroupKeys.SFTP) {
        permission = {
          toggleState: ThreeWayToggle.VALUES.ALL,
          permissions: permissionGroups[permissionGroupKeys.ROSTER_UPLOAD]
            .reduce(addGroupPermission, {})
        };
        // toggle the Roster Upload toggle on because this permission is required with SFTP
        this.permissions = Object.assign({}, this.permissions, {
          [permissionGroupKeys.ROSTER_UPLOAD]: permission
        });
        this.save();
      }

      permission = {
        toggleState: ThreeWayToggle.VALUES.ALL,
        permissions: permissionGroups[permissionGroupKey]
          .reduce(addGroupPermission, {})
      };
    }
    else if (state === ThreeWayToggle.VALUES.NONE) {
      permission = {
        toggleState: ThreeWayToggle.VALUES.NONE,
        permissions: permissionGroups[permissionGroupKey]
          .reduce(removeGroupPermission, {})
      };
    }
    this.permissions = Object.assign({}, this.permissions, {
      [permissionGroupKey]: permission
    });
    this.save();
  }

  sponsor(e) {
    const groupId = this.selectedGroup > 0 ? this.selectedGroup : this.groupId;

    this.isSponsor = e.target.checked;
    if (this.isSponsor) {
      this.selectedGroup = groupId;
      if (this.selectedGroup === 0) {
        this.selectedGroup = this.groupId;
      }
      e.target.parentElement.parentElement.querySelector('#hide-student-toggle').checked = false;
      this.isHideStudent = false;
    } else if (this.selectedGroupLevel === 0) {
      this.selectedGroup = 0;
    }
    this.save();
  }

  hideStudent(e) {
    const groupId = this.selectedGroup > 0 ? this.selectedGroup : this.groupId;

    this.isHideStudent = e.target.checked;
    if (this.isHideStudent) {
      this.selectedGroup = groupId;
      if (this.selectedGroup === 0) {
        this.selectedGroup = this.groupId;
      }
      e.target.parentElement.parentElement.querySelector('#sponsor-toggle').checked = false;
      this.isSponsor = false;
    } else if (this.selectedGroupLevel === 0) {
      this.selectedGroup = 0;
    }
    this.save();
  }

  save() {
    this.roleExists = false;
    this.genericApiError = false;

    const permissions = {};
    const restricted = restrictedPermissions(this.selectedGroupLevel, parseInt(this.tiersLevels, 10) + 1);

    if (this.isSponsor) {
      permissions.sponsor = true;
    } else if (this.isHideStudent) {
      permissions.hideStudent = true;
    } else {
      for (const [key, val] of Object.entries(this.permissions)) {
        if (typeof(val.permissions) !== 'undefined') {
          for (const [permissionKey, permissionValue] of Object.entries(val.permissions)) {
            // if this permissionKey is not restricted, go ahead and set it
            if (restricted.indexOf(permissionKey) === -1) {
              permissions[permissionKey] = permissionValue;
            }
          }
        }
      }
    }

    try {
      this.permissions.group = this.selectedGroup;
      this.permissions.sponsor = this.isSponsor;
      this.permissions.hideStudent = this.isHideStudent;
      this.permissions.tiersLevels = this.tiersLevels;
      this.permissions.groupLevel = this.selectedGroupLevel;

      storeInstance.commit({namespace: sdoNs.ADMIN_UINFO, mutationId: 'createRoles', payload: {permissions: this.permissions, index: this.index}});
    } catch (e) {
      if (e.error.code === exceptionCodes.INPUT_DUPLICATE) {
        this.roleExists = true;
      } else {
        this.genericApiError = true;
      }
      /** @todo handle role create error */
    }

    window.Appcues && window.Appcues.track('adminRoleCreated');
  }

  discard() {
    storeInstance.commit({
      namespace: sdoNs.ADMIN_UINFO,
      mutationId: 'removeNewRole',
      payload: this.index
    });
  }
  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default RoleCrudBlock;
